import {
  PermissionCode,
  emptyOnboardingTemplateDefinition,
  onboardingAssigneeKindSchema,
  onboardingDueAnchorSchema,
  onboardingTaskKindSchema,
  onboardingTemplateDefinitionSchema,
  type OnboardingAssigneeKind,
  type OnboardingDueAnchor,
  type OnboardingTaskKind,
  type OnboardingTemplateDefinition,
  type OrgUnit,
  type RoleOption,
  type TeamSummary,
  type OnboardingAssigneeUser,
} from '@gb-ticket/shared-types';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { onboardingPanelClassName } from '@/products/onboarding/features/onboarding-panel';
import { mergeOrgOptions } from '@/platform/features/users/org-select-options';
import { OnboardingTaskFields } from '@/products/onboarding/features/onboarding-task-fields';
import { OnboardingTemplateDraftDialog } from '@/products/onboarding/features/onboarding-template-draft-dialog';
import {
  ONBOARDING_ASSIGNEE_KIND_LABEL,
  ONBOARDING_CASE_KIND_LABEL,
  ONBOARDING_DUE_ANCHOR_LABEL,
  ONBOARDING_TASK_KIND_LABEL,
  ONBOARDING_TEMPLATE_VERSION_STATUS_LABEL,
} from '@/products/onboarding/features/onboarding-template-labels';
import { useOnboardingAssigneeOptions } from '@/products/onboarding/features/use-onboarding-assignee-options';
import {
  downloadOnboardingJson,
  onboardingTemplateExportFilename,
} from '@/products/onboarding/features/download-onboarding-json';
import {
  useExportOnboardingTemplate,
  useOnboardingTemplate,
  usePublishOnboardingTemplate,
  useRetireOnboardingTemplate,
  useReplaceOnboardingTemplateDraft,
  useUpdateOnboardingTemplate,
} from '@/products/onboarding/features/use-onboarding-templates';
import { OnboardingTemplateDeleteButton } from '@/products/onboarding/features/onboarding-template-delete-button';
import { ONBOARDING_TEMPLATES_PATH } from '@/products/onboarding/onboarding-paths';
import {
  OnboardingFlowCanvas,
  type FlowSelection,
} from '@/products/onboarding/features/onboarding-flow-canvas';
import {
  addPhase,
  addTask,
  flowToDefinition,
  materializeLayout,
  removePhase,
  removeTask,
  type FlowPhase,
  type FlowTask,
} from '@/products/onboarding/features/onboarding-flow-layout';

const TASK_KINDS = onboardingTaskKindSchema.options;
const ASSIGNEE_KINDS = onboardingAssigneeKindSchema.options;
const ANCHORS = onboardingDueAnchorSchema.options;

type EditorTask = FlowTask;
type EditorPhase = FlowPhase;

export function OnboardingTemplateDetailPage() {
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const me = useMe();
  const canManage = hasPermission(me.data, PermissionCode.ONBOARDING_TEMPLATES_MANAGE);
  const template = useOnboardingTemplate(id);
  const update = useUpdateOnboardingTemplate(id);
  const replaceDraft = useReplaceOnboardingTemplateDraft(id);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [pendingPhaseId, setPendingPhaseId] = useState<string | null>(null);
  const [pendingTaskKey, setPendingTaskKey] = useState<string | null>(null);
  const publish = usePublishOnboardingTemplate(id);
  const retire = useRetireOnboardingTemplate(id);
  const exportTemplate = useExportOnboardingTemplate();

  const detail = template.data;
  usePageTitle(detail?.name ? `Vorlage: ${detail.name}` : 'Vorlage');

  const draftVersion = useMemo(
    () => detail?.versions.find((version) => version.status === 'draft') ?? null,
    [detail],
  );
  const publishedVersion = useMemo(
    () =>
      detail?.versions.find((version) => version.id === detail.currentPublishedVersionId) ?? null,
    [detail],
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [phases, setPhases] = useState<EditorPhase[]>([]);
  const [hydratedId, setHydratedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selection, setSelection] = useState<FlowSelection>(null);

  // Re-hydrate editor state when the loaded template changes (React: adjust state while rendering).
  if (detail && hydratedId !== detail.id) {
    const source =
      draftVersion?.definition ??
      publishedVersion?.definition ??
      emptyOnboardingTemplateDefinition();
    setHydratedId(detail.id);
    setName(detail.name);
    setDescription(detail.description ?? '');
    setPhases(toEditorPhases(source));
  }

  if (!canManage) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  if (template.isError && isForbidden(template.error)) {
    return <p className="text-sm text-muted-foreground">{copy('onboarding.access.denied')}</p>;
  }

  if (template.isPending) {
    return <p className="text-sm text-muted-foreground">Vorlage wird geladen …</p>;
  }

  if (template.isError || !detail) {
    return (
      <p className="text-sm text-destructive">
        {template.error instanceof ApiRequestError
          ? template.error.message
          : 'Die Vorlage konnte nicht geladen werden.'}
      </p>
    );
  }

  async function saveDraft() {
    setActionError(null);
    const definition = buildDefinition(phases);
    const parsed = onboardingTemplateDefinitionSchema.safeParse(definition);
    if (!parsed.success) {
      setActionError(parsed.error.issues[0]?.message ?? 'Die Definition ist ungültig.');
      return;
    }
    try {
      await update.mutateAsync({
        name: name.trim(),
        description: description.trim() === '' ? null : description.trim(),
        definition: parsed.data,
      });
      toast.success('Entwurf gespeichert.');
      setHydratedId(null);
    } catch (caught) {
      setActionError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Der Entwurf konnte nicht gespeichert werden.',
      );
    }
  }

  async function onPublish() {
    setActionError(null);
    try {
      await publish.mutateAsync();
      toast.success('Vorlage veröffentlicht.');
      setHydratedId(null);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.error.code === 'ONBOARDING_TEMPLATE_CYCLE') {
        setActionError(caught.message);
        return;
      }
      setActionError(
        caught instanceof ApiRequestError ? caught.message : 'Veröffentlichen ist fehlgeschlagen.',
      );
    }
  }

  async function onExport() {
    setActionError(null);
    try {
      const document = await exportTemplate.mutateAsync(id);
      downloadOnboardingJson(document, onboardingTemplateExportFilename(name));
    } catch (caught) {
      setActionError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Die Vorlage konnte nicht exportiert werden.',
      );
    }
  }

  async function onRetire() {
    setActionError(null);
    try {
      await retire.mutateAsync();
      toast.success('Veröffentlichte Version zurückgezogen.');
      setHydratedId(null);
    } catch (caught) {
      setActionError(
        caught instanceof ApiRequestError ? caught.message : 'Zurückziehen ist fehlgeschlagen.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title={detail.name}
        description={`${ONBOARDING_CASE_KIND_LABEL[detail.kind]} · Entwurf bearbeiten`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => void onExport()}
              disabled={exportTemplate.isPending}
            >
              Exportieren
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link to={ONBOARDING_TEMPLATES_PATH}>Zur Liste</Link>
            </Button>
            <OnboardingTemplateDeleteButton
              template={detail}
              onDeleted={() => navigate(ONBOARDING_TEMPLATES_PATH)}
            />
          </div>
        }
      />

      <p className={`${onboardingPanelClassName} text-sm text-muted-foreground`}>
        Entwurf:{' '}
        {draftVersion
          ? `Version ${draftVersion.versionNumber} (${ONBOARDING_TEMPLATE_VERSION_STATUS_LABEL.draft})`
          : 'kein Entwurf (Speichern legt die nächste Version an)'}
        {publishedVersion
          ? ` · Veröffentlicht: Version ${publishedVersion.versionNumber}`
          : ' · keine veröffentlichte Version'}
        {detail.isActive ? '' : ' · inaktiv'}
      </p>

      <OnboardingTemplateDraftDialog
        key={draftOpen ? 'draft-open' : 'draft-closed'}
        mode="replace"
        open={draftOpen}
        pending={replaceDraft.isPending}
        error={draftError}
        onOpenChange={(open) => {
          setDraftOpen(open);
          if (!open) {
            setDraftError(null);
          }
        }}
        onCreate={() => undefined}
        onReplace={(body) => {
          setDraftError(null);
          void replaceDraft
            .mutateAsync(body)
            .then(() => {
              setDraftOpen(false);
              setHydratedId(null);
              toast.success('Entwurf ersetzt.');
            })
            .catch((caught: unknown) => {
              setDraftError(
                caught instanceof ApiRequestError
                  ? caught.message
                  : 'Der Entwurf konnte nicht ersetzt werden.',
              );
            });
        }}
      />

      {actionError ? (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => void saveDraft()} disabled={update.isPending}>
          Entwurf speichern
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setDraftError(null);
            setDraftOpen(true);
          }}
        >
          Entwurf aus Beschreibung
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void onPublish()}
          disabled={publish.isPending || !draftVersion}
        >
          Veröffentlichen
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => void onRetire()}
          disabled={retire.isPending || !detail.currentPublishedVersionId}
        >
          Zurückziehen
        </Button>
      </div>

      <div className={`${onboardingPanelClassName} grid gap-4 sm:grid-cols-2`}>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="tpl-name">Name</Label>
          <Input id="tpl-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="tpl-description">Beschreibung</Label>
          <Textarea
            id="tpl-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
          />
        </div>
      </div>

      <div
        className={
          selection
            ? 'grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]'
            : 'grid items-start gap-4'
        }
      >
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPhases((current) => addPhase(current))}
            >
              Phase hinzufügen
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setPhases((current) =>
                  addTask(
                    current,
                    selection?.type === 'phase'
                      ? selection.id
                      : selection?.type === 'task'
                        ? (current.find((phase) =>
                            phase.tasks.some((task) => task.key === selection.key),
                          )?.id ?? null)
                        : null,
                    emptyTask(current),
                  ),
                )
              }
            >
              Aufgabe hinzufügen
            </Button>
          </div>
          <OnboardingFlowCanvas
            phases={phases}
            editable
            onPhasesChange={setPhases}
            onSelect={setSelection}
            onReject={setActionError}
          />
        </div>
        {selection ? (
          <aside className="space-y-3 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto">
            {selection?.type === 'phase'
              ? phases
                  .filter((phase) => phase.id === selection.id)
                  .map((phase) => (
                    <div key={phase.id} className={`${onboardingPanelClassName} space-y-3`}>
                      <div className="space-y-2">
                        <Label htmlFor={`phase-title-${phase.id}`}>Phase</Label>
                        <Input
                          id={`phase-title-${phase.id}`}
                          value={phase.title}
                          onChange={(event) =>
                            setPhases((current) =>
                              current.map((entry) =>
                                entry.id === phase.id
                                  ? { ...entry, title: event.target.value }
                                  : entry,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Lage</Label>
                        <Select
                          value={phase.stage}
                          onValueChange={(value) =>
                            setPhases((current) =>
                              current.map((entry) =>
                                entry.id === phase.id
                                  ? { ...entry, stage: value as EditorPhase['stage'] }
                                  : entry,
                              ),
                            )
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="preboarding">Vorbereitung</SelectItem>
                            <SelectItem value="onboarding">Eintritt</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        className="min-h-11"
                        onClick={() => setPendingPhaseId(phase.id)}
                      >
                        Phase entfernen
                      </Button>
                    </div>
                  ))
              : null}
            {selection?.type === 'task'
              ? phases.flatMap((phase) =>
                  phase.tasks
                    .filter((task) => task.key === selection.key)
                    .map((task) => (
                      <TaskEditor
                        key={task.key}
                        task={task}
                        onChange={(patch) => {
                          setPhases((current) =>
                            current.map((entry) => ({
                              ...entry,
                              tasks: entry.tasks.map((item) =>
                                item.key === task.key ? { ...item, ...patch } : item,
                              ),
                            })),
                          );
                          if (patch.key && patch.key !== task.key) {
                            setSelection({ type: 'task', key: patch.key });
                          }
                        }}
                        onRemove={() => setPendingTaskKey(task.key)}
                      />
                    )),
                )
              : null}
          </aside>
        ) : null}
      </div>
      <PhaseRemoveDialog
        phase={phases.find((phase) => phase.id === pendingPhaseId) ?? null}
        phases={phases}
        onCancel={() => setPendingPhaseId(null)}
        onConfirm={() => {
          if (!pendingPhaseId) {
            return;
          }
          setPhases((current) => removePhase(current, pendingPhaseId));
          setSelection(null);
          setPendingPhaseId(null);
          toast.success(
            'Phase aus dem Entwurf entfernt. Der Entwurf muss noch gespeichert werden.',
          );
        }}
      />
      <TaskRemoveDialog
        taskKey={pendingTaskKey}
        phases={phases}
        onCancel={() => setPendingTaskKey(null)}
        onConfirm={() => {
          if (!pendingTaskKey) {
            return;
          }
          setPhases((current) => removeTask(current, pendingTaskKey));
          setSelection(null);
          setPendingTaskKey(null);
          toast.success(
            'Aufgabe aus dem Entwurf entfernt. Der Entwurf muss noch gespeichert werden.',
          );
        }}
      />
    </div>
  );
}

function PhaseRemoveDialog({
  phase,
  phases,
  onCancel,
  onConfirm,
}: {
  phase: EditorPhase | null;
  phases: readonly EditorPhase[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const removedKeys = new Set(phase?.tasks.map((task) => task.key) ?? []);
  const dependents = phases.flatMap((entry) =>
    entry.tasks.filter(
      (task) => !removedKeys.has(task.key) && task.dependsOn.some((key) => removedKeys.has(key)),
    ),
  );
  return (
    <AlertDialog open={phase != null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Phase „{phase?.title ?? ''}“ entfernen?</AlertDialogTitle>
          <AlertDialogDescription>
            Die Phase und ihre Aufgaben verschwinden nur aus diesem Entwurf. Laufende und
            abgeschlossene Vorgänge behalten ihren gespeicherten Stand. Der Entwurf muss danach noch
            gespeichert werden.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {phase && phase.tasks.length > 0 ? (
          <div className="space-y-2 text-sm">
            <p>Aufgaben in dieser Phase:</p>
            <ul className="list-disc space-y-1 pl-5">
              {phase.tasks.map((task) => (
                <li key={task.key}>
                  {task.title}
                  {task.required ? ' · Pflicht' : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Die Phase enthält keine Aufgaben.</p>
        )}
        {dependents.length > 0 ? (
          <p className="text-sm">
            Abhängigkeiten fallen weg bei: {dependents.map((task) => task.title).join(', ')}.
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          <Button type="button" variant="destructive" className="min-h-11" onClick={onConfirm}>
            Aus dem Entwurf entfernen
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function TaskRemoveDialog({
  taskKey,
  phases,
  onCancel,
  onConfirm,
}: {
  taskKey: string | null;
  phases: readonly EditorPhase[];
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const task = phases.flatMap((phase) => phase.tasks).find((item) => item.key === taskKey) ?? null;
  const dependents = phases.flatMap((phase) =>
    phase.tasks.filter((item) => taskKey != null && item.dependsOn.includes(taskKey)),
  );
  return (
    <AlertDialog open={task != null} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Aufgabe „{task?.title ?? ''}“ entfernen?</AlertDialogTitle>
          <AlertDialogDescription>
            Die Aufgabe wird aus dem Entwurf gelöscht, nicht aus einem laufenden Vorgang. Eine
            Pflicht in der Vorlage gilt danach dort nicht mehr. Der Entwurf muss noch gespeichert
            werden.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {dependents.length > 0 ? (
          <p className="text-sm">
            Diese Aufgaben verlieren die Abhängigkeit:{' '}
            {dependents.map((item) => item.title).join(', ')}.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Keine andere Aufgabe hängt davon ab.</p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          <Button type="button" variant="destructive" className="min-h-11" onClick={onConfirm}>
            Aus dem Entwurf entfernen
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function TaskEditor({
  task,
  onChange,
  onRemove,
}: {
  task: EditorTask;
  onChange: (patch: Partial<EditorTask>) => void;
  onRemove?: () => void;
}) {
  const [personQ, setPersonQ] = useState('');
  const needsPick =
    task.assignee === 'user' ||
    task.assignee === 'role' ||
    task.assignee === 'department' ||
    task.assignee === 'team';
  const options = useOnboardingAssigneeOptions({
    page: 1,
    pageSize: 50,
    includeInactive: true,
    q: task.assignee === 'user' ? personQ || undefined : undefined,
  });

  const departments = mergeOrgOptions(
    options.data?.departments ?? [],
    currentOrg(options.data?.departments, task.assigneeDepartmentId),
  );
  const teams = mergeOrgOptions(
    options.data?.teams ?? [],
    currentOrg(options.data?.teams, task.assigneeTeamId),
  );
  const roles = options.data?.roles ?? [];
  const people = ensureCurrentPerson(options.data?.users.items ?? [], task.assigneeUserId);

  return (
    <div className={`${onboardingPanelClassName} space-y-3`}>
      <div className="grid gap-3">
        <div className="space-y-2">
          <Label>Schlüssel</Label>
          <Input value={task.key} onChange={(event) => onChange({ key: event.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Titel</Label>
          <Input value={task.title} onChange={(event) => onChange({ title: event.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Anleitung</Label>
          <Textarea
            value={task.instructions ?? ''}
            onChange={(event) =>
              onChange({ instructions: event.target.value === '' ? null : event.target.value })
            }
            rows={2}
          />
        </div>
        <div className="space-y-2">
          <Label>Art</Label>
          <Select
            value={task.kind}
            onValueChange={(value) => onChange({ kind: value as OnboardingTaskKind })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TASK_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {ONBOARDING_TASK_KIND_LABEL[kind]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Zuständigkeit</Label>
          <Select
            value={task.assignee}
            onValueChange={(value) => onChange({ assignee: value as OnboardingAssigneeKind })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSIGNEE_KINDS.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {ONBOARDING_ASSIGNEE_KIND_LABEL[kind]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {needsPick && options.isPending ? (
          <p className="text-sm text-muted-foreground">Wird geladen …</p>
        ) : null}
        {needsPick && options.isError ? (
          <p className="text-sm text-destructive">
            {options.error instanceof ApiRequestError
              ? options.error.message
              : 'Die Auswahl konnte nicht geladen werden.'}
          </p>
        ) : null}
        {task.assignee === 'user' ? (
          <div className="space-y-2">
            <Label htmlFor={`person-search-${task.key}`}>Person</Label>
            <Input
              id={`person-search-${task.key}`}
              value={personQ}
              onChange={(event) => setPersonQ(event.target.value)}
              placeholder="Suche nach Name oder E-Mail"
            />
            <Select
              value={task.assigneeUserId ?? undefined}
              onValueChange={(value) => onChange({ assigneeUserId: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Person wählen" />
              </SelectTrigger>
              <SelectContent>
                {people.length === 0 ? (
                  <SelectItem value="__empty" disabled>
                    Keine Treffer.
                  </SelectItem>
                ) : (
                  people.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.displayName}
                      {person.email ? ` · ${person.email}` : ''}
                      {!person.isActive ? ' (archiviert)' : ''}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {task.assignee === 'role' ? (
          <div className="space-y-2">
            <Label>Gruppe</Label>
            <Select
              value={task.assigneeRoleId ?? undefined}
              onValueChange={(value) => onChange({ assigneeRoleId: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Gruppe wählen" />
              </SelectTrigger>
              <SelectContent>
                {roles.length === 0 ? (
                  <SelectItem value="__empty" disabled>
                    Keine Treffer.
                  </SelectItem>
                ) : (
                  roles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      {roleLabel(role, roles)}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {task.assignee === 'department' ? (
          <div className="space-y-2">
            <Label>Abteilung</Label>
            <Select
              value={task.assigneeDepartmentId ?? undefined}
              onValueChange={(value) => onChange({ assigneeDepartmentId: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Abteilung wählen" />
              </SelectTrigger>
              <SelectContent>
                {departments.map((department) => (
                  <SelectItem key={department.id} value={department.id}>
                    {orgLabel(department, departments)}
                    {!department.isActive ? ' (archiviert)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        {task.assignee === 'team' ? (
          <div className="space-y-2">
            <Label>Team</Label>
            <Select
              value={task.assigneeTeamId ?? undefined}
              onValueChange={(value) => onChange({ assigneeTeamId: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Team wählen" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {orgLabel(team, teams)}
                    {!team.isActive ? ' (archiviert)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <div className="space-y-2">
          <Label>Anker</Label>
          <Select
            value={task.anchor}
            onValueChange={(value) => onChange({ anchor: value as OnboardingDueAnchor })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ANCHORS.map((anchor) => (
                <SelectItem key={anchor} value={anchor}>
                  {ONBOARDING_DUE_ANCHOR_LABEL[anchor]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Offset (Tage)</Label>
          <Input
            type="number"
            value={task.offsetDays}
            onChange={(event) => onChange({ offsetDays: Number(event.target.value) || 0 })}
          />
        </div>
        <div className="space-y-2">
          <Label>Abhängigkeiten</Label>
          <p className="text-sm text-muted-foreground">
            {task.dependsOn.length > 0 ? task.dependsOn.join(', ') : 'Keine'}
          </p>
        </div>
        {task.kind === 'data' || task.kind === 'review' ? (
          <OnboardingTaskFields
            mode="definition"
            fields={task.dataFields}
            onChange={(dataFields) => onChange({ dataFields })}
          />
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={task.required}
            onCheckedChange={(checked) => onChange({ required: checked === true })}
          />
          Pflicht
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={task.visibleToSubject}
            onCheckedChange={(checked) => onChange({ visibleToSubject: checked === true })}
          />
          Für betroffene Person sichtbar
        </label>
        {onRemove ? (
          <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={onRemove}>
            Aufgabe entfernen
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function currentOrg<T extends OrgUnit | TeamSummary>(
  list: T[] | undefined,
  id: string | null | undefined,
): T | null {
  if (!id || !list) {
    return null;
  }
  return list.find((item) => item.id === id) ?? null;
}

function orgLabel(item: { name: string; slug: string }, all: { name: string; slug: string }[]) {
  const duplicates = all.filter((entry) => entry.name === item.name).length > 1;
  return duplicates ? `${item.name} (${item.slug})` : item.name;
}

function roleLabel(role: RoleOption, roles: RoleOption[]) {
  const duplicates = roles.filter((entry) => entry.name === role.name).length > 1;
  return duplicates ? `${role.name} (${role.code})` : role.name;
}

function ensureCurrentPerson(
  items: OnboardingAssigneeUser[],
  currentId: string | null | undefined,
): OnboardingAssigneeUser[] {
  if (!currentId) {
    return items;
  }
  if (items.some((item) => item.id === currentId)) {
    return items;
  }
  return [
    {
      id: currentId,
      email: '',
      displayName: currentId,
      isActive: false,
    },
    ...items,
  ];
}

function toEditorPhases(definition: OnboardingTemplateDefinition): EditorPhase[] {
  return materializeLayout(definition);
}

function buildDefinition(phases: EditorPhase[]): OnboardingTemplateDefinition {
  return flowToDefinition(phases);
}

function emptyTask(phases: readonly EditorPhase[]): EditorTask {
  const keys = new Set(phases.flatMap((phase) => phase.tasks.map((task) => task.key)));
  let index = phases.reduce((sum, phase) => sum + phase.tasks.length, 0) + 1;
  let key = `task_${index}`;
  while (keys.has(key)) {
    index += 1;
    key = `task_${index}`;
  }
  return {
    key,
    title: 'Neue Aufgabe',
    instructions: null,
    required: true,
    kind: 'confirm',
    assignee: 'process_owner',
    assigneeUserId: null,
    assigneeRoleId: null,
    assigneeDepartmentId: null,
    assigneeTeamId: null,
    offsetDays: 0,
    anchor: 'entry',
    dependsOn: [],
    visibleToSubject: false,
    dataFields: [],
    x: 0,
    y: 0,
  };
}

function isForbidden(error: unknown): boolean {
  return error instanceof ApiRequestError && error.error.statusCode === 403;
}
